import { useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

interface ScrollPosition {
  x: number;
  y: number;
  footerTop?: number;
}

const positions = new Map<string, ScrollPosition>();
const scrollInputs = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

function readPosition(): ScrollPosition {
  const footer = document.querySelector('[data-page-footer]')?.getBoundingClientRect();
  return {
    x: window.scrollX,
    y: window.scrollY,
    // Keep the footer at the same viewport position as content above it loads.
    footerTop: footer && footer.top < window.innerHeight && footer.bottom > 0
      ? footer.top
      : undefined,
  };
}

export function useMarketingScrollRestoration() {
  const { key, pathname, search, hash } = useLocation();
  // Native anchor links can reuse the default router key for different URLs.
  const positionKey = `${key}:${pathname}${search}${hash}`;
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previous; };
  }, []);

  useLayoutEffect(() => {
    const saved = navigationType === 'POP' ? positions.get(positionKey) : undefined;
    let restoring = Boolean(saved || hash);
    let frame = 0;
    let observer: ResizeObserver | undefined;

    const remember = () => {
      if (!restoring) positions.set(positionKey, readPosition());
    };

    const restore = () => {
      const footer = saved?.footerTop !== undefined ? document.querySelector('[data-page-footer]') : null;
      if (saved) {
        const top = footer
          ? window.scrollY + footer.getBoundingClientRect().top - saved.footerTop!
          : saved.y;
        window.scrollTo({ left: saved.x, top, behavior: 'instant' });
      } else if (hash) {
        let id = hash.slice(1);
        try { id = decodeURIComponent(id); } catch { /* Use the literal anchor if malformed. */ }
        document.getElementById(id)?.scrollIntoView({ behavior: 'instant' });
      } else {
        window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      }
    };

    const stopRestoring = () => {
      restoring = false;
      observer?.disconnect();
      cancelAnimationFrame(frame);
      scrollInputs.forEach(event => window.removeEventListener(event, stopRestoring));
      remember();
    };

    restore();
    remember();
    window.addEventListener('scroll', remember, { passive: true });

    if (restoring) {
      observer = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(restore);
      });
      observer.observe(document.body);
      scrollInputs.forEach(event => window.addEventListener(event, stopRestoring, { passive: true }));
    }

    return () => {
      observer?.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', remember);
      scrollInputs.forEach(event => window.removeEventListener(event, stopRestoring));
    };
  }, [positionKey, hash, navigationType]);
}
